/**
 * Lifecycle Investment Service
 *
 * Handles API calls to the Lifecycle Investment System backend.
 */

import axios from 'axios';

const API_BASE_URL = 'https://xhl99la4a7.execute-api.us-east-2.amazonaws.com/dev';

// Types
export interface UserProfile {
  current_age: number;
  retirement_age: number;
  life_expectancy?: number;
  current_savings: number;
  annual_income: number;
  annual_expenses: number;
  emergency_fund_months?: number;
  filing_status?: string;
  custom_tax_rate?: number | null;
  ss_monthly_benefit?: number;
  ss_start_age?: number;
  roth_conversion_enabled?: boolean;
  pre_tax_percentage?: number;
  roth_target_bracket?: number;
  rmd_start_age?: number;
}

export interface Assumptions {
  inflation_rate?: number;
  savings_return?: number;
  growth_return?: number;
  dividend_return?: number;
  super_long_term_return?: number;
  b1_return?: number;
  b2_return?: number;
  b3_return?: number;
  b4_return?: number;
  contribution_growth_rate?: number;
}

export interface AllocationWeights {
  savings: number;
  growth: number;
  dividend: number;
  super_long_term: number;
}

export interface SimulationInput {
  profile: UserProfile;
  assumptions?: Assumptions;
  allocation?: AllocationWeights;
  annual_contribution: number;
  stress_mode?: 'none' | 'fast' | 'slow' | 'both';
  stress_drawdown_pct?: number;  // 0.30 = 30% drawdown
  stress_timing?: 'pre_retirement' | 'early_retirement' | 'mid_retirement';
  include_traditional_comparison?: boolean;
}

export interface BucketState {
  name: string;
  balance: number;
  return_rate: number;
  contribution: number;
  withdrawal: number;
  growth: number;
}

export interface YearlySnapshot {
  year: number;
  age: number;
  phase: 'accumulation' | 'transition' | 'distribution';
  buckets: { [key: string]: BucketState };
  total_balance: number;
  total_contribution: number;
  total_withdrawal: number;
  total_growth: number;
  cumulative_contributions: number;
  cumulative_withdrawals: number;
  tax_amount: number;
  net_withdrawal: number;
  ss_income: number;
  roth_conversion: number;
}

export interface StressScenario {
  name: string;
  drawdown_pct: number;
  recovery_years: number;
  impact_year: number;
  pre_stress_balance: number;
  post_stress_balance: number;
  recovery_balance: number;
  bucket_survival: { [key: string]: boolean };
  years_to_depletion: number | null;
  survived: boolean;
}

export interface KPIs {
  years_to_retirement: number;
  total_contributions: number;
  projected_retirement_balance: number;
  glide_path_years: number;
  transition_start_age: number;
  annual_retirement_income: number;
  income_replacement_ratio: number;
  years_of_income: number;
  legacy_amount: number;
  effective_tax_rate: number;
  annual_net_retirement_income: number;
  total_ss_income: number;
  total_roth_conversions: number;
  roth_conversion_tax_cost: number;
  bucket_readiness: { [key: string]: boolean };
  stress_survival_fast: boolean;
  stress_survival_slow: boolean;
}

export interface TraditionalProjection {
  year: number;
  age: number;
  phase: string;
  total_balance: number;
  total_withdrawal: number;
  total_growth: number;
  blended_return: number;
  stock_pct: number;
  bond_pct: number;
  ss_income: number;
  tax_amount: number;
  net_withdrawal: number;
}

export interface TraditionalKPIs {
  projected_retirement_balance: number;
  annual_retirement_income: number;
  years_of_income: number;
  legacy_amount: number;
  effective_return: number;
  portfolio_depleted_age: number | null;
}

export interface TraditionalStressResult {
  name: string;
  drawdown_pct: number;
  recovery_years: number;
  pre_stress_balance: number;
  post_stress_balance: number;
  recovery_balance: number;
  years_to_depletion: number | null;
  survived: boolean;
}

export interface TraditionalComparison {
  strategy_name: string;
  strategy_id: string;
  yearly_projections: TraditionalProjection[];
  kpis: TraditionalKPIs;
  stress_results: TraditionalStressResult[];
}

export interface SimulationResult {
  run_id: string;
  user_id: string;
  timestamp: string;
  inputs: SimulationInput;
  kpis: KPIs;
  yearly_projections: YearlySnapshot[];
  stress_results: StressScenario[];
  traditional_comparisons?: TraditionalComparison[];
  computation_time_ms: number;
  version: string;
}

export interface ChatResponse {
  answer: string;
  numbers_used: { [key: string]: any };
  provider: string;
  confidence: number;
  grounded: boolean;
}

class LifecycleService {
  /**
   * Run a lifecycle simulation
   */
  async runSimulation(token: string, input: SimulationInput): Promise<SimulationResult> {
    const response = await axios.post(
      `${API_BASE_URL}/simulate`,
      {
        profile: input.profile,
        assumptions: input.assumptions || {},
        allocation: input.allocation || {},
        annual_contribution: input.annual_contribution,
        stress_mode: input.stress_mode || 'both',
        stress_drawdown_pct: input.stress_drawdown_pct ?? 0.30,
        stress_timing: input.stress_timing || 'pre_retirement',
        include_traditional_comparison: true
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }

  /**
   * Chat with AI about simulation results
   */
  async chat(token: string, runId: string, question: string, context?: {
    kpis?: KPIs;
    inputs?: SimulationInput;
    stress_results?: StressScenario[];
    chat_history?: Array<{ question: string; answer: string }>;
  }): Promise<ChatResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/chat`,
      {
        run_id: runId,
        question: question,
        ...(context && { context })
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  }
}

export const lifecycleService = new LifecycleService();
